import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../../../config/api';

const calculateAge = (dobString) => {
    if (!dobString) return null;
    const today = new Date();
    const birthDate = new Date(dobString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const getAgeGroup = (age) => {
    if (age === null || age === undefined || isNaN(age)) return "";
    if (age <= 12) return "Kids (0-12)";
    if (age <= 19) return "Teens (13-19)";
    if (age <= 59) return "Adults (20-59)";
    return "Seniors (60+)";
};

const inputStyle = {
    borderRadius: '10px', border: '1px solid #e0e0e0',
    fontSize: '0.9rem', padding: '10px 14px',
    color: '#2c3e50', backgroundColor: '#fafafa'
};
const labelStyle = {
    fontSize: '0.72rem', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.8px',
    color: '#b0b8c1', marginBottom: '6px', display: 'block'
};

const ManualEntryForm = ({ onSuccess, onCancel }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverMsg, setServerMsg] = useState('');
    const [isError, setIsError] = useState(false);

    const today = new Date().toISOString().split('T')[0];
    const currentTime = new Date().toTimeString().slice(0, 5);

    const [formData, setFormData] = useState({
        fullName: '', mobileNumber: '', email: '', dob: '',
        address: '', area: '', city: '', state: '', pincode: '',
        gender: '', ageGroup: '', occupation: '', organizationName: '',
        purposeOfVisit: '', notes: '', branch: '',
        visitDate: today, visitTime: currentTime,
    });

    const [otherPurpose, setOtherPurpose] = useState('');
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "dob") {
            const age = calculateAge(value);
            const ageGroup = getAgeGroup(age);
            setFormData(prev => ({
                ...prev,
                dob: value,
                ageGroup: ageGroup
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // Autofill city and state from pincode using api.postalpincode.in
    useEffect(() => {
        const pincodeVal = (formData.pincode || "").trim();
        if (/^\d{6}$/.test(pincodeVal)) {
            const fetchLocation = async () => {
                try {
                    const response = await fetch(`https://api.postalpincode.in/pincode/${pincodeVal}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data && data[0] && data[0].Status === "Success" && data[0].PostOffice && data[0].PostOffice[0]) {
                            const postOffice = data[0].PostOffice[0];
                            const city = postOffice.District || "";
                            const state = postOffice.State || "";
                            setFormData(prev => ({
                                ...prev,
                                city: city,
                                state: state
                            }));
                            setErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.city;
                                delete newErrors.state;
                                delete newErrors.pincode;
                                return newErrors;
                            });
                        } else {
                            setErrors(prev => ({ ...prev, pincode: "Enter correct pincode" }));
                            setFormData(prev => ({ ...prev, city: "", state: "" }));
                        }
                    } else {
                        setErrors(prev => ({ ...prev, pincode: "Enter correct pincode" }));
                        setFormData(prev => ({ ...prev, city: "", state: "" }));
                    }
                } catch (error) {
                    console.error("Error fetching pincode data:", error);
                    setErrors(prev => ({ ...prev, pincode: "Enter correct pincode" }));
                    setFormData(prev => ({ ...prev, city: "", state: "" }));
                }
            };
            fetchLocation();
        } else {
            setErrors(prev => {
                if (prev.pincode === "Enter correct pincode" || prev.pincode === "Invalid Pincode (6 digits required)") {
                    const newErrors = { ...prev };
                    delete newErrors.pincode;
                    return newErrors;
                }
                return prev;
            });
        }
    }, [formData.pincode]);

    const validate = () => {
        const newErrors = {};
        if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
        if (!formData.mobileNumber.trim() || !/^\d{10}$/.test(formData.mobileNumber.trim()))
            newErrors.mobileNumber = 'Valid 10-digit mobile number is required';
        if (!formData.branch) newErrors.branch = 'Branch is required';
        if (!formData.visitDate) newErrors.visitDate = 'Visit date is required';
        if (!formData.visitTime) newErrors.visitTime = 'Visit time is required';
        if (!formData.purposeOfVisit) newErrors.purposeOfVisit = 'Purpose of visit is required';
        if (formData.purposeOfVisit === 'Other' && !otherPurpose.trim())
            newErrors.purposeOfVisit = 'Please describe the purpose';
        if (formData.dob) {
            const birthDate = new Date(formData.dob);
            const today = new Date();
            if (birthDate > today) {
                newErrors.dob = 'Date of Birth cannot be in the future';
            } else if (today.getFullYear() - birthDate.getFullYear() > 120) {
                newErrors.dob = 'Invalid Date of Birth';
            }
        }
        if (formData.pincode && formData.pincode.trim()) {
            const pincodeRegex = /^\d{6}$/;
            if (!pincodeRegex.test(formData.pincode.trim())) {
                newErrors.pincode = 'Invalid Pincode (6 digits required)';
            } else if (errors.pincode === 'Enter correct pincode') {
                newErrors.pincode = 'Enter correct pincode';
            }
        }
        return newErrors;
    };

    const handleSubmit = async () => {
        setServerMsg('');
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                ...formData,
                purposeOfVisit: formData.purposeOfVisit === 'Other' ? otherPurpose.trim() : formData.purposeOfVisit,
            };

            const res = await fetch(API_ENDPOINTS.VISITORS.MANUAL_ENTRY, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await res.json();
            if (res.ok) {
                setServerMsg(`✅ Entry created! Visitor ID: ${data.visitor?.visitorId}`);
                setIsError(false);
                setTimeout(() => {
                    onSuccess && onSuccess();
                }, 2000);
            } else {
                setServerMsg(data.message || 'Something went wrong.');
                setIsError(true);
            }
        } catch (err) {
            setServerMsg('Network error. Please try again.');
            setIsError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const sectionStyle = {
        fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase',
        color: '#ac2e62', letterSpacing: '1px', marginBottom: '12px', marginTop: '20px'
    };

    return (
        <div style={{ padding: '10px' }}>
            {serverMsg && (
                <Alert variant={isError ? 'danger' : 'success'} className="mb-3">{serverMsg}</Alert>
            )}

            {/* VISITOR INFORMATION */}
            <div style={sectionStyle}>👤 Visitor Information</div>
            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Full Name *</Form.Label>
                        <Form.Control type="text" name="fullName" value={formData.fullName} onChange={handleChange} style={inputStyle} placeholder="Full Name" isInvalid={!!errors.fullName} />
                        <Form.Control.Feedback type="invalid">{errors.fullName}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Mobile Number *</Form.Label>
                        <Form.Control type="text" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} style={inputStyle} placeholder="10-digit number" maxLength={10} isInvalid={!!errors.mobileNumber} />
                        <Form.Control.Feedback type="invalid">{errors.mobileNumber}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Email</Form.Label>
                        <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} style={inputStyle} placeholder="email@example.com" />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Gender</Form.Label>
                        <Form.Select name="gender" value={formData.gender} onChange={handleChange} style={inputStyle}>
                            <option value="">Select</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Date of Birth</Form.Label>
                        <Form.Control
                            type="date"
                            name="dob"
                            value={formData.dob}
                            onChange={handleChange}
                            isInvalid={!!errors.dob}
                            style={inputStyle}
                            max={new Date().toISOString().split("T")[0]}
                        />
                        <Form.Control.Feedback type="invalid">{errors.dob}</Form.Control.Feedback>
                    </Form.Group>
                    {formData.dob && (
                        <div style={{ marginTop: "-8px", marginBottom: "15px", fontSize: "0.85rem", color: "#ac2e62", fontWeight: "600" }}>
                            Age: {calculateAge(formData.dob)} years
                        </div>
                    )}
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Occupation</Form.Label>
                        <Form.Control type="text" name="occupation" value={formData.occupation} onChange={handleChange} style={inputStyle} placeholder="Occupation" />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Organization</Form.Label>
                        <Form.Control type="text" name="organizationName" value={formData.organizationName} onChange={handleChange} style={inputStyle} placeholder="Organization name" />
                    </Form.Group>
                </Col>
            </Row>

            {/* LOCATION DETAILS */}
            <div style={sectionStyle}>📍 Location Details</div>
            <Row>
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Pincode</Form.Label>
                        <Form.Control type="text" name="pincode" value={formData.pincode} onChange={handleChange} style={inputStyle} placeholder="Pincode" maxLength={6} isInvalid={!!errors.pincode} />
                        <Form.Control.Feedback type="invalid">{errors.pincode}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>City</Form.Label>
                        <Form.Control type="text" name="city" value={formData.city} onChange={handleChange} style={inputStyle} placeholder="City" />
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>State</Form.Label>
                        <Form.Control type="text" name="state" value={formData.state} onChange={handleChange} style={inputStyle} placeholder="State" />
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Area</Form.Label>
                        <Form.Control type="text" name="area" value={formData.area} onChange={handleChange} style={inputStyle} placeholder="Area" />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Address</Form.Label>
                        <Form.Control type="text" name="address" value={formData.address} onChange={handleChange} style={inputStyle} placeholder="Full address" />
                    </Form.Group>
                </Col>
            </Row>

            {/* VISIT DETAILS */}
            <div style={sectionStyle}>📅 Visit Details</div>
            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Branch *</Form.Label>
                        <Form.Select name="branch" value={formData.branch} onChange={handleChange} style={inputStyle} isInvalid={!!errors.branch}>
                            <option value="">-- Select Branch --</option>
                            <option value="Maainagari">Maainagari</option>
                            <option value="Shree Manashanti Chhatralaya">Shree Manashanti Chhatralaya</option>
                            <option value="TMGF Central Office">TMGF Central Office</option>
                            <option value="Maainagari - Solapur">Maainagari - Solapur</option>
                            <option value="Maainagari - Goa">Maainagari - Goa</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.branch}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Visit Date *</Form.Label>
                        <Form.Control type="date" name="visitDate" value={formData.visitDate} onChange={handleChange} style={inputStyle} max={today} isInvalid={!!errors.visitDate} />
                        <Form.Control.Feedback type="invalid">{errors.visitDate}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Visit Time *</Form.Label>
                        <Form.Control type="time" name="visitTime" value={formData.visitTime} onChange={handleChange} style={inputStyle} isInvalid={!!errors.visitTime} />
                        <Form.Control.Feedback type="invalid">{errors.visitTime}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={8}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Purpose of Visit *</Form.Label>
                        <Form.Select name="purposeOfVisit" value={formData.purposeOfVisit} onChange={handleChange} style={inputStyle} isInvalid={!!errors.purposeOfVisit}>
                            <option value="">-- Select Purpose --</option>
                            <option value="General Inquiry">General Inquiry</option>
                            <option value="Volunteer Interest">Volunteer Interest</option>
                            <option value="Donation Related">Donation Related</option>
                            <option value="Event Participation">Event Participation</option>
                            <option value="Partnership Discussion">Partnership Discussion</option>
                            <option value="NGO Visit">NGO Visit</option>
                            <option value="Other">Other</option>
                        </Form.Select>
                        {formData.purposeOfVisit === 'Other' && (
                            <Form.Control type="text" placeholder="Describe purpose..." value={otherPurpose}
                                onChange={(e) => setOtherPurpose(e.target.value)} style={{ ...inputStyle, marginTop: '8px' }} />
                        )}
                        <Form.Control.Feedback type="invalid">{errors.purposeOfVisit}</Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label style={labelStyle}>Notes</Form.Label>
                        <Form.Control type="text" name="notes" value={formData.notes} onChange={handleChange} style={inputStyle} placeholder="Optional notes..." />
                    </Form.Group>
                </Col>
            </Row>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-3 mt-4">
                <Button variant="outline-secondary" onClick={onCancel} style={{ borderRadius: '10px', padding: '10px 24px', fontWeight: '600' }}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={isSubmitting}
                    style={{ backgroundColor: '#ac2e62', border: 'none', borderRadius: '10px', padding: '10px 28px', fontWeight: '700' }}>
                    {isSubmitting ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : '+ Add Entry'}
                </Button>
            </div>
        </div>
    );
};

export default ManualEntryForm;